import { Button } from "primereact/button";
import { Card } from "primereact/card";
import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { RoutePaths, StatusesColors } from "src/resources/enum";
import DoughnutChart from "src/components/Charts/DoughnutChart";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import { ProgressSpinner } from "primereact/progressspinner";
import { formatNumber } from "src/utils/helpers";

const PreventiveMaintenanceAppointmentsCard = () => {
  const { t } = useTranslation(["common", "dashboard"]);
  const navigate = useNavigate();
  const {
    completedExceedingSLA,
    completedWithInSLA,
    openExceedingSLA,
    totalCount,
  } = useSelector(
    (state: any) => state.dashboard.maintenanceAppointmentsStatistics
  );
  const [chartsData, setChartsData] = useState<any>();
  const { isMaintenanceAppointmentsStatisticsLoaded } = useSelector(
    (state: any) => state.dashboard
  );
  const language = useSelector((state: any) => state.lookup.language);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    cutout: "83%",
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
    rotation: -90, // Start point
    circumference: 180,
  };
  useEffect(() => {
    const valuesArray = [
      completedWithInSLA,
      completedExceedingSLA,
      openExceedingSLA,
    ];

    if (valuesArray.some((value) => value > 0)) {
    const newChartsData = {
      data: valuesArray,
      color: [
        StatusesColors.Completed,
        StatusesColors.Exceeded,
        StatusesColors.OpenAndExceeding,
      ],
    } as any;

    setChartsData(newChartsData);
    }
    else {
      const newChartsData = {
        data: [1],
        color: [StatusesColors.Planck],
      } as any;
      setChartsData(newChartsData);
    }
  }, [
    completedWithInSLA,
    completedExceedingSLA,
    openExceedingSLA,
    totalCount,
    isMaintenanceAppointmentsStatisticsLoaded,
    language,
    t,
  ]);

  return (
    <Card className="card card-3-1">
      <div className="card-content">
        <div className="d-flex gap-2 justify-content-between align-items-center pt-2">
          <h6 className="card-title">
            {t("preventiveMaintenanceAppointments")}
          </h6>
          <Button
            label={t("viewAppointments")}
            className="edit-btn light-primary-btn m-0"
            onClick={() => navigate(RoutePaths.PreventiveAppointment)}
          />
        </div>
        {isMaintenanceAppointmentsStatisticsLoaded &&
          chartsData?.data?.length && (
            <div className="d-flex mb-2 align-items-center justify-content-between m-auto mt-5">
              <div className="d-flex gap-1 align-items-center justify-content-center flex-column w-50">
                <div className="w-75">
                  <DoughnutChart
                    chartData={{
                      labels: chartsData.label,
                      datasets: [
                        {
                          data: chartsData.data,
                          backgroundColor: chartsData.color,
                        },
                      ],
                    }}
                    options={chartOptions}
                    centerText={[
                      {
                        text: formatNumber(totalCount),
                        fontSize: 40,
                        fontWeight: "normal",
                        xOffset: 0,
                        yOffset: +20,
                      },
                      {
                        text: "Appointments",
                        fontSize: 14,
                        fontWeight: "normal",
                        xOffset: 0,
                        yOffset: +50,
                      },
                    ]}
                  />
                </div>
              </div>
              <div className="data w-50">
                <div className="d-flex align-items-center gap-5 pb-2 appointments-stats">
                  <h3 className="w-25" title={completedWithInSLA}>
                    {formatNumber(completedWithInSLA)}
                  </h3>
                  <div className="d-flex gap-2 w-75 justify-content-start align-items-baseline">
                    <span className="circle completed small-circle"></span>
                    <span>{t("completedWithinTheSLA")}</span>
                  </div>
                </div>
                <HorizontalDivider />
                <div className="d-flex align-items-center gap-5 pb-2 mt-3 appointments-stats">
                  <h3 className="w-25" title={completedExceedingSLA}>
                    {formatNumber(completedExceedingSLA)}
                  </h3>
                  <div className="d-flex gap-2 w-75 justify-content-start align-items-baseline">
                    <span className="circle canceled small-circle"></span>
                    <span>{t("completedExceedingSLA")}</span>
                  </div>
                </div>
                <HorizontalDivider />
                <div className="d-flex align-items-center gap-5 pb-2 mt-3 appointments-stats">
                  <h3 className="w-25" title={openExceedingSLA}>
                    {formatNumber(openExceedingSLA)}
                  </h3>
                  <div className="d-flex gap-2 w-75 justify-content-start align-items-baseline">
                    <span className="circle chart-orange small-circle"></span>
                    <span>{t("openAppointmentsThatAreExceedingTheSLA")}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        {!isMaintenanceAppointmentsStatisticsLoaded && (
          <div className="d-flex justify-content-center">
            <ProgressSpinner />
          </div>
        )}
      </div>
    </Card>
  );
};

export default PreventiveMaintenanceAppointmentsCard;
